import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CmgUiUtilities } from 'apps/cmg-clear2-ui/src/app/cmg-1003/components/common/cmg-ui-utils';
import { ConfirmationModalCloseType } from 'apps/cmg-clear2-ui/src/app/models';
import { NotificationService } from 'apps/cmg-clear2-ui/src/app/services/notification.service';
import { UnsavedChangesDialogResponse } from 'apps/cmg-clear2-ui/src/app/shared/models';
import { SpinnerService } from 'apps/cmg-clear2-ui/src/app/shared/services/spinner.service';
import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';
import { DialogService } from 'primeng/dynamicdialog';
import { catchError, combineLatest, delay, filter, finalize, map, of, shareReplay, Subject, switchMap, take, tap } from 'rxjs';
import { Models } from '../../../models/global.models';
import { RouteApiService } from '../../../stores/api/route-api.service';
import { BorrowerLeadQuery } from '../../../stores/lead/borrower-lead.query';
import { LeadQuery } from '../../../stores/lead/lead.query';
import { LoanOfficerInformationLeadQuery } from '../../../stores/lead/loan-officer-lead.query';
import { LoanOfficerNotesLeadQuery } from '../../../stores/lead/loan-officer-notes-lead.query';
import { PreferredTimeContactLeadQuery } from '../../../stores/lead/preferred-time-contact-lead.query';
import { SourceInformationLeadQuery } from '../../../stores/lead/source-information-lead.query';
import { SubjectPropertyInformationLeadQuery } from '../../../stores/lead/subject-property-information-lead.query';

@Injectable({
  providedIn: 'root',
})
export class LeadFormV2Service {
  private readonly _router = inject(Router);
  private readonly _dialogService = inject(DialogService);
  private readonly _api = inject(RouteApiService);
  private readonly _spinnerService = inject(SpinnerService);
  private readonly _notificationService = inject(NotificationService);
  private readonly _leadQuery = inject(LeadQuery);
  private readonly _loanOfficerInformationLeadQuery = inject(LoanOfficerInformationLeadQuery);
  private readonly _borrowerLeadQuery = inject(BorrowerLeadQuery);
  private readonly _preferredTimeContactLeadQuery = inject(PreferredTimeContactLeadQuery);
  private readonly _subjectPropertyInformationLeadQuery = inject(SubjectPropertyInformationLeadQuery);
  private readonly _sourceInformationLeadQuery = inject(SourceInformationLeadQuery);
  private readonly _loanOfficerNotesLeadQuery = inject(LoanOfficerNotesLeadQuery);
  private readonly _environment = inject(APP_ENV);

  processUnsavedChangesSub: Subject<string> = new Subject<string>();
  processUnsavedChanges$ = this.processUnsavedChangesSub.asObservable();
  processUnvalidFormSub: Subject<boolean> = new Subject<boolean>();
  processUnvalidForm$ = this.processUnvalidFormSub.asObservable();
  markFormsAsPristineSub = new Subject<boolean>();
  markFormsAsPristine$ = this.markFormsAsPristineSub.asObservable();
  processSaveLeadSub = new Subject<{ saveBtnClicked: boolean; nextRoute?: string }>();
  processSaveLead$ = this.processSaveLeadSub.asObservable().pipe(
    filter((val) => val.saveBtnClicked),
    map((val) => val.nextRoute),
    switchMap((nextRoute) => this.saveLead$(nextRoute)),
    finalize(() => {
      this.processSaveLeadSub.next({ saveBtnClicked: false, nextRoute: null });
    }),
  );

  referralTypes$ = this._api.getReferralTypes$().pipe(
    // This map is a workaround to fix a value coming from the endpoint.
    map((referralTypes) => {
      return referralTypes.map((referralType) => {
        if (referralType.referralType === 'Desk Rental') {
          referralType.referralType = referralType.referralType.replace(/\s/g, '');
        }
        return referralType;
      });
    }),
    shareReplay(),
  );

  private _leadForm$ = combineLatest({
    loanOfficerInformation: this._loanOfficerInformationLeadQuery.select(),
    borrowerInformation: this._borrowerLeadQuery.select(),
    preferredTimeContact: this._preferredTimeContactLeadQuery.select(),
    subjectPropertyInformation: this._subjectPropertyInformationLeadQuery.select(),
    sourceInformation: this._sourceInformationLeadQuery.select(),
    loanOfficerNotes: this._loanOfficerNotesLeadQuery.select(),
  }).pipe(take(1));

  private _leadFormValid$ = this._leadForm$.pipe(
    map((leadForm) => {
      return (
        leadForm.borrowerInformation.valid &&
        leadForm.loanOfficerInformation.valid &&
        leadForm.loanOfficerNotes.valid &&
        leadForm.preferredTimeContact.valid &&
        leadForm.sourceInformation.valid &&
        leadForm.subjectPropertyInformation.valid
      );
    }),
  );

  private _leadFormDirty$ = this._leadForm$.pipe(
    map((leadForm) => {
      return (
        leadForm.borrowerInformation.dirty ||
        leadForm.loanOfficerInformation.dirty ||
        leadForm.loanOfficerNotes.dirty ||
        leadForm.preferredTimeContact.dirty ||
        leadForm.sourceInformation.dirty ||
        leadForm.subjectPropertyInformation.dirty
      );
    }),
  );

  validateSaveLead$ = this._leadFormValid$.pipe(
    switchMap((valid) => {
      if (valid) {
        return this.saveLead$();
      } else {
        this.processUnvalidFormSub.next(true);
        return of('invalid');
      }
    }),
    finalize(() => {
      this.processUnvalidFormSub.next(false);
    }),
  );

  saveLead$ = (navigateTo?: string) =>
    combineLatest([
      this._leadQuery.select(),
      this._loanOfficerInformationLeadQuery.select(),
      this._borrowerLeadQuery.select(),
      this._preferredTimeContactLeadQuery.select(),
      this._subjectPropertyInformationLeadQuery.select(),
      this._sourceInformationLeadQuery.select(),
    ]).pipe(
      take(1),
      map(([lead, loanOfficerInformation, borrower, preferredTimeToContact, subjectPropertyInformation, sourceInformation]) => {
        return this._applyExplicitNulls({
          ...lead,
          ...loanOfficerInformation,
          ...borrower,
          ...preferredTimeToContact,
          ...subjectPropertyInformation,
          ...sourceInformation,
        });
      }),
      switchMap((lead) => {
        // In Edit mode, guid is not inclided in the Api response
        // So the saved guid in the UI store will be needed for saving notes and Toast message
        return lead.guid
          ? this._api.leads.patch(lead).pipe(
              map((res) => {
                return { lead: res, guidEditMode: lead?.guid };
              }),
            )
          : this._api.leads.post(lead).pipe(
              map((res) => {
                return { lead: res, guidEditMode: lead?.guid };
              }),
            );
      }),
      switchMap((res) =>
        this._loanOfficerNotesLeadQuery.select().pipe(
          take(1),
          map((val) => {
            return {
              lead: res.lead,
              guidEditMode: res.guidEditMode,
              leadNotes: val.leadNotes,
            };
          }),
        ),
      ),
      switchMap((val) => {
        // In Edit mode val.guidEditMode must have a value, if not it is create mode
        const leadNotesPayload: Partial<Models.LeadNote> = {
          leadGUID: val.guidEditMode ? val.guidEditMode : val.lead.guid,
          content: val.leadNotes ? val.leadNotes : '',
        };
        return this._api.saveLeadNotes(leadNotesPayload).pipe(map(() => val));
      }),
      tap(() => {
        this._spinnerService.show('Saving Lead...');
      }),
      delay(this._environment.retail.newLoanRefreshDelayMS),
      tap((leadData) => {
        this._spinnerService.hide();
        this.markFormsAsPristineSub.next(true);
        this._notificationService.showToastSub.next({
          severity: 'success',
          summary: `Lead ${leadData.guidEditMode ? 'Saved' : 'Created'}`,
          detail: `Lead has been successfully ${!leadData.guidEditMode ? `created as #${leadData.lead.leadNumber}` : 'saved'}`,
          life: 5000,
        });
        const route = navigateTo ? [navigateTo] : ['/pipeline/lead-form/', leadData.guidEditMode ? leadData.guidEditMode : leadData.lead.guid];
        this._router.navigate(route);
      }),
      catchError((error) => {
        this._spinnerService.hide();
        this._notificationService.showToastSub.next({
          severity: 'error',
          summary: 'Error',
          detail: error?.error?.message ?? error?.error?.Detail,
          life: 5000,
        });
        return of(error);
      }),
    );

  showUnsavedChangesDialog(closable = false) {
    const result$ = new Subject<UnsavedChangesDialogResponse>();
    this._leadFormDirty$.subscribe((isDirty) => {
      if (isDirty) {
        const dialogRef = CmgUiUtilities.showUnsavedChangesModal(
          this._dialogService,
          `
          <p class="mt-0">Are you sure you want to leave this panel without saving?</p>
          <p class="mt-0">All changes will be lost if they are not saved.</p>
          `,
          'Save and Continue',
          'Continue without Saving',
          closable,
        );
        dialogRef.onClose.subscribe((modalResult: ConfirmationModalCloseType) => {
          if (modalResult === ConfirmationModalCloseType.ClosedViaAcceptButton) {
            result$.next(UnsavedChangesDialogResponse.saveAndContinue);
          } else if (modalResult === ConfirmationModalCloseType.ClosedViaRejectButton) {
            result$.next(UnsavedChangesDialogResponse.cancelAndContinue);
          } else {
            /** Undefined - user closed it via x button */
            result$.next(UnsavedChangesDialogResponse.dismissViaXButton);
          }
        });
      } else {
        setTimeout(() => {
          result$.next(UnsavedChangesDialogResponse.noNeedForDialog);
        });
      }
    });
    return result$.asObservable();
  }

  /**
   * Transforms `undefined` and empty string to `null` to ensure data is saved accurately.
   *
   * API does special treatment when saving Lead form:
   * 1) `null` is actually saved as `null`.
   * 2) `undefined` is ignored - i.e. no update to that data.
   * 3) Empty string (`''`) is saved as-is, but we'll transform it to `null` beforehand.
   */
  private _applyExplicitNulls(lead: Models.Lead) {
    Object.keys(lead).forEach((key) => {
      if (lead[key] === undefined || lead[key] === '') {
        lead[key] = null;
      }
    });

    return lead;
  }
}
